import {
  ArrowUpRight,
  BookOpenText,
  Boxes,
  Building2,
  Calculator,
  ContactRound,
  Files,
  Gavel,
  Library,
  Scale,
  ScanSearch,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildInstrumentaToolUrl,
  INSTRUMENTA_TOOLS,
  persistRecentInstrumentaTool,
  readRecentInstrumentaTools,
} from "./instrumenta-tools.js";

const toolIcons = {
  normas: Scale,
  jurisprudencia: ScanSearch,
  doctrina: BookOpenText,
  biblioteca: Library,
  "procesador-documental": Files,
  "calculadora-intereses": Calculator,
  "consulta-fiscal": Building2,
  "matriculas-profesionales": ContactRound,
  "buscador-sentencias": Gavel,
};

function ToolLink({ instrumentaUrl, onUse, tool, recent = false }) {
  const Icon = toolIcons[tool.id] || Boxes;
  return (
    <a
      className={`suite-tools-card ${recent ? "is-recent" : ""}`}
      href={buildInstrumentaToolUrl(instrumentaUrl, tool.path)}
      aria-label={`${tool.label}, abrir en Instrumenta`}
      onClick={() => onUse(tool.id)}
    >
      <span className="suite-tools-card-icon" aria-hidden="true">
        <Icon size={19} />
      </span>
      <span className="suite-tools-card-copy">
        <strong>{tool.label}</strong>
        <small>{tool.description}</small>
      </span>
      <ArrowUpRight size={16} aria-hidden="true" />
    </a>
  );
}

export function InstrumentaToolsDialog({
  allowedToolIds = null,
  instrumentaUrl,
  onClose,
  open,
}) {
  const [recentEntries, setRecentEntries] = useState([]);
  const closeButtonRef = useRef(null);
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current = document.activeElement;
    setRecentEntries(readRecentInstrumentaTools());
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocusRef.current?.focus?.();
    };
  }, [onClose, open]);

  const availableTools = useMemo(() => {
    if (!Array.isArray(allowedToolIds)) return INSTRUMENTA_TOOLS;
    const allowed = new Set(allowedToolIds);
    return INSTRUMENTA_TOOLS.filter((tool) => allowed.has(tool.accessId));
  }, [allowedToolIds]);

  const { recentTools, remainingTools } = useMemo(() => {
    const recentIds = new Set(recentEntries.map((entry) => entry.id));
    return {
      recentTools: recentEntries
        .map((entry) => availableTools.find((tool) => tool.id === entry.id))
        .filter(Boolean),
      remainingTools: availableTools.filter((tool) => !recentIds.has(tool.id)),
    };
  }, [availableTools, recentEntries]);

  if (!open) return null;

  const handleUse = (toolId) => {
    setRecentEntries(persistRecentInstrumentaTool(toolId));
  };

  const keepFocusInside = (event) => {
    if (event.key !== "Tab") return;
    const focusable = dialogRef.current?.querySelectorAll(
      "a[href], button:not([disabled])",
    );
    if (!focusable?.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="suite-modal-backdrop suite-tools-backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <div
        ref={dialogRef}
        className="suite-dialog suite-tools-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="suite-tools-title"
        onKeyDown={keepFocusInside}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="suite-tools-header">
          <span className="suite-tools-header-icon" aria-hidden="true">
            <Boxes size={21} />
          </span>
          <span>
            <strong id="suite-tools-title">Herramientas</strong>
            <small>Elegí una herramienta para abrirla en Instrumenta.</small>
          </span>
          <button
            ref={closeButtonRef}
            type="button"
            className="suite-icon-button suite-tools-close"
            aria-label="Cerrar herramientas"
            onClick={onClose}
          >
            <X size={19} />
          </button>
        </header>

        <div className="suite-tools-content">
          <section
            className="suite-tools-section"
            aria-labelledby="suite-tools-recent-title"
          >
            <h3 id="suite-tools-recent-title">Usadas recientemente</h3>
            {recentTools.length ? (
              <div className="suite-tools-grid suite-tools-grid--recent">
                {recentTools.map((tool) => (
                  <ToolLink
                    key={tool.id}
                    instrumentaUrl={instrumentaUrl}
                    onUse={handleUse}
                    recent
                    tool={tool}
                  />
                ))}
              </div>
            ) : (
              <p className="suite-tools-empty">
                Tus herramientas recientes aparecerán acá.
              </p>
            )}
          </section>

          <section
            className="suite-tools-section"
            aria-labelledby="suite-tools-all-title"
          >
            <h3 id="suite-tools-all-title">
              {recentTools.length ? "Más herramientas" : "Todas las herramientas"}
            </h3>
            <div className="suite-tools-grid">
              {remainingTools.map((tool) => (
                <ToolLink
                  key={tool.id}
                  instrumentaUrl={instrumentaUrl}
                  onUse={handleUse}
                  tool={tool}
                />
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
